window.customElements.define(
  'custom-sticky-add-to-cart-one-time',
  class CustomStickyAddToCartOneTime extends window.HTMLElement {
    connectedCallback() {
      this.initDOMElements();
      this.initEventListeners();
      this.initVisibilityObserver();
    }

    disconnectedCallback() {
      this.removeEventListeners();
      this.removeVisibilityObserver();
    }

    // Initialize DOM elements
    initDOMElements() {
      this.form = this.querySelector('form');
      this.stickyVariantLabel = this.querySelector('#sticky-variant-label');
      this.stickyVariantSelect = this.querySelector('#sticky-variant-select');
      this.stickyCompareAtPrice = this.querySelectorAll('#sticky-compare-at-price');
      this.stickyPrice = this.querySelectorAll('#sticky-price');
      this.stickySavings = this.querySelector('#sticky-savings');
      this.stickySavingsContainer = this.querySelector('#sticky-savings-container');
      this.stickyAddToCartButton = this.querySelector('#sticky-add-to-cart-button');
      this.stickyNotifyMeButton = this.querySelector('#sticky-notify-me-button');
      this.productForm = document.querySelector('.custom-variant-picker__form');

      if (!this.productForm) {
        return;
      }
    }

    // Event Listeners
    initEventListeners() {
      this.form.addEventListener('change', this.handleChange.bind(this));
      this.form.addEventListener('submit', this.handleSubmit.bind(this));

      // Listen for variant picker changes
      window.addEventListener('custom-variant-picker:change', this.handleVariantPickerChange.bind(this));
    }

    removeEventListeners() {
      this.form.removeEventListener('change', this.handleChange.bind(this));
      this.form.removeEventListener('submit', this.handleSubmit.bind(this));

      // Remove variant picker listener
      window.removeEventListener('custom-variant-picker:change', this.handleVariantPickerChange.bind(this));
    }

    // Visibility Observer
    initVisibilityObserver() {
      this.visibilityObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              this.classList.add('hidden');
            } else {
              const formRect = this.productForm.getBoundingClientRect();
              if (formRect.top < 0) {
                this.classList.remove('hidden');
              }
            }
          });
        },
        {
          threshold: 0.1,
          rootMargin: '0px 0px -50px 0px',
        }
      );

      this.visibilityObserver.observe(this.productForm);
    }

    removeVisibilityObserver() {
      if (this.visibilityObserver) {
        this.visibilityObserver.disconnect();
      }
    }

    // Handlers
    handleVariantPickerChange(event) {
      const { variant } = event.detail;

      if (!variant.variantId) return;
      const variantSelectOption = this.form.querySelector(`select option[value="${variant.variantId}"]`);

      if (!variantSelectOption) return;
      variantSelectOption.selected = true;

      const currentVariantData = this.getCurrentVariantData();
      this.handleUpdateUI(currentVariantData);
      this.handleUpdateUrl(currentVariantData);
    }

    handleChange(event) {
      const currentVariantData = this.getCurrentVariantData();

      this.handleUpdateUI(currentVariantData);
      this.handleUpdateUrl(currentVariantData);

      this.dispatchEvent(
        new CustomEvent('custom-sticky-add-to-cart:change', {
          detail: {
            variant: {
              available: currentVariantData.available,
              variantId: currentVariantData.variantId,
              price: currentVariantData.price,
              compareAtPrice: currentVariantData.compareAtPrice,
            },
            element: event.target,
          },
          bubbles: true,
        })
      );
    }

    async handleSubmit(event) {
      event.preventDefault();
      const currentVariantData = this.getCurrentVariantData();

      try {
        this.stickyAddToCartButton.classList.add('is-loading');
        await this.addToCart({
          id: currentVariantData.variantId,
          quantity: 1,
        });

        this.dispatchCartRefresh();
      } catch (error) {
        console.error('Failed to add item to cart:', error);
      } finally {
        this.stickyAddToCartButton.classList.remove('is-loading');
      }
    }

    handleUpdateUI(currentVariantData) {
      if (this.stickyPrice) {
        this.stickyPrice.forEach((price) => {
          price.textContent = currentVariantData.price;
        });
      }

      if (this.stickyCompareAtPrice) {
        this.stickyCompareAtPrice.forEach((price) => {
          price.textContent = currentVariantData.compareAtPrice;
        });
      }

      if (this.stickySavings) {
        this.stickySavings.textContent = currentVariantData.savings;
      }

      if (this.stickySavingsContainer) {
        const hasSavings =
          currentVariantData.price !== currentVariantData.compareAtPrice &&
          currentVariantData.compareAtPrice !== '' &&
          currentVariantData.compareAtPrice !== currentVariantData.price;
        this.stickySavingsContainer.classList.toggle('hidden', !hasSavings);

        if (this.stickyCompareAtPrice) {
          this.stickyCompareAtPrice.forEach((price) => {
            price.classList.toggle('hidden', !hasSavings);
          });
        }
      }

      if (this.stickyVariantLabel) {
        this.stickyVariantLabel.textContent = currentVariantData.currentLabel;
      }

      if (this.stickyAddToCartButton) {
        this.stickyAddToCartButton.classList.toggle('hidden', !currentVariantData.available);
      }

      if (this.stickyNotifyMeButton) {
        this.stickyNotifyMeButton.classList.toggle('hidden', currentVariantData.available);
      }
    }

    handleUpdateUrl(currentVariantData) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', currentVariantData.variantId);
      window.history.replaceState({}, '', url);
    }

    // Utility functions
    async addToCart(item) {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ items: [item] }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message);
      }

      const result = await response.json();

      return result;
    }

    dispatchCartRefresh() {
      document.dispatchEvent(new CustomEvent('cart:refresh'));
      document.dispatchEvent(new CustomEvent('variant:add'));
    }

    getCurrentVariantData() {
      const currentVariant = this.form.querySelector('select option:checked');
      const { variantId, price, compareAtPrice, savings, currentLabel, available } = currentVariant.dataset;

      return {
        available: available === 'true',
        variantId,
        price,
        compareAtPrice,
        savings,
        currentLabel,
      };
    }
  }
);
